import React from 'react';

/**
 * Dreamy Bedtime logo mark — a crescent moon cradling a sleepy star,
 * surrounded by soft sparkles on a deep-purple rounded square.
 *
 * Props:
 *   size    — px size of the icon square (default 36)
 *   variant — 'full' (icon + wordmark) | 'icon' (icon only) | 'wordmark-dark' | 'wordmark-light'
 *   className / style — forwarded to the wrapper
 */
const Logo = ({ size = 36, variant = 'full', className = '', style = {} }) => {
  const iconSize = size;
  const radius   = iconSize * 0.22;   // corner radius

  const Icon = (
    <svg
      width={iconSize}
      height={iconSize}
      viewBox="0 0 48 48"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }}
      aria-label="Dreamy Bedtime logo"
    >
      <defs>
        {/* Background gradient */}
        <linearGradient id="db-bg" x1="0" y1="0" x2="48" y2="48" gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="#2d1b69" />
          <stop offset="100%" stopColor="#4a2d8a" />
        </linearGradient>
        {/* Moon gradient — warm gold */}
        <linearGradient id="db-moon" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#fde68a" />
          <stop offset="100%" stopColor="#f59e0b" />
        </linearGradient>
        {/* Star gradient */}
        <linearGradient id="db-star" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#fff9c4" />
          <stop offset="100%" stopColor="#fde68a" />
        </linearGradient>
        {/* Drop shadow filter */}
        <filter id="db-glow" x="-20%" y="-20%" width="140%" height="140%">
          <feGaussianBlur stdDeviation="1.5" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>
      </defs>

      {/* ── Background rounded square ── */}
      <rect width="48" height="48" rx={radius} ry={radius} fill="url(#db-bg)" />

      {/* ── Subtle inner glow ring ── */}
      <rect width="46" height="46" x="1" y="1" rx={radius - 1} ry={radius - 1}
        fill="none" stroke="rgba(111,66,193,0.5)" strokeWidth="1" />

      {/* ── Moon body (full circle, warm gold) ── */}
      <circle cx="22" cy="25" r="11" fill="url(#db-moon)" />

      {/* ── Crescent cut-out (same as background to create crescent illusion) ── */}
      <circle cx="27" cy="20" r="9" fill="url(#db-bg)" />

      {/* ── Sleepy star nestled in crescent curve ── */}
      {/* Star shape using a path */}
      <g transform="translate(28.5, 20)" filter="url(#db-glow)">
        <path
          d="M0,-4 L1,-1 L4,-1 L1.8,1 L2.5,4 L0,2 L-2.5,4 L-1.8,1 L-4,-1 L-1,-1 Z"
          fill="url(#db-star)"
          transform="scale(0.85)"
        />
      </g>

      {/* ── Small sparkle dots ── */}
      {/* Top-right sparkle */}
      <circle cx="37" cy="12" r="1.5" fill="#c9a8ff" opacity="0.85" />
      <circle cx="39" cy="16" r="1"   fill="#c9a8ff" opacity="0.6"  />
      <circle cx="34" cy="9"  r="1"   fill="#fde68a" opacity="0.7"  />

      {/* Bottom-left sparkle */}
      <circle cx="10" cy="36" r="1.5" fill="#c9a8ff" opacity="0.7"  />
      <circle cx="7"  cy="32" r="1"   fill="#fde68a" opacity="0.5"  />

      {/* Bottom-right sparkle */}
      <circle cx="38" cy="36" r="1.2" fill="#c9a8ff" opacity="0.6"  />
      <circle cx="36" cy="40" r="0.8" fill="#fde68a" opacity="0.5"  />

      {/* ── Zzz dots below moon — "dreamy" sleep hint ── */}
      <circle cx="18" cy="37" r="1.2" fill="rgba(201,168,255,0.6)" />
      <circle cx="22" cy="39" r="0.9" fill="rgba(201,168,255,0.45)" />
      <circle cx="26" cy="40" r="0.7" fill="rgba(201,168,255,0.35)" />
    </svg>
  );

  if (variant === 'icon') return (
    <span className={className} style={{ display: 'inline-flex', ...style }}>{Icon}</span>
  );

  const isLight = variant === 'wordmark-light';
  const nameColor    = isLight ? '#ffffff'       : '#2d1b69';
  const taglineColor = isLight ? 'rgba(255,255,255,0.7)' : '#6f42c1';
  const fontSize     = Math.round(iconSize * 0.6);
  const taglineSize  = Math.round(iconSize * 0.3);

  return (
    <span
      className={className}
      style={{ display: 'inline-flex', alignItems: 'center', gap: Math.round(iconSize * 0.25) + 'px', textDecoration: 'none', ...style }}
    >
      {Icon}
      <span style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
        <span style={{
          fontFamily: "'Fredoka One', cursive",
          fontWeight: 400,
          fontSize: fontSize + 'px',
          color: nameColor,
          letterSpacing: '-0.3px',
          whiteSpace: 'nowrap',
        }}>
          Dreamy Bedtime
        </span>
        {iconSize >= 32 && (
          <span style={{
            fontSize: taglineSize + 'px',
            color: taglineColor,
            fontWeight: 500,
            letterSpacing: '0.02em',
          }}>
            bedtime stories, reimagined
          </span>
        )}
      </span>
    </span>
  );
};

export default Logo;
